<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { FUND_STAGES, agentName, currentView, fundLps, fundStageLabel, userEmail } from '../lib/stores';
  import {
    getFundCalendar, getFundCalendarIndex, type FundCalendar, type FundCalendarIndex,
    type FundCalendarIndexMeeting, type FundCalendarInvestor, type FundCalendarMeeting,
  } from '../lib/api/fund-calendar';
  import { addFundNote, downloadFundCallBriefPdf, setFundFollowUp } from '../lib/api/fund-ir';
  import { canUseAs, isSupportView } from '../lib/supportView';
  import { localDay } from '../lib/format';
  import MiniMonth from '../lib/calendar/MiniMonth.svelte';
  import { callBriefView, providerName, safeUrl, type CallBriefView } from '../lib/calendar/brief';
  import {
    addDays, addMonths, coordination, hourRange, initials, isLogged, isPast, layoutDay, lpName, meetingName,
    meetingStatus, monthGrid, monthStart, overlapGroups, overlapKey, ownerColors, sameDay, weekStart, type CoordinationRow,
  } from '../lib/calendar/week';

  const HOUR_PX = 56;  // change with .cal-hour height
  const SEARCH_WEEKS_BACK = 8;  // of 26, the index route's limit
  const SEARCH_SHOWN = 8;
  const INDEX_TTL = 5 * 60_000;
  const MONTH_CHIPS = 3;
  type Mode = 'week' | 'month' | 'agenda';
  const VIEWS = [['week', 'Week', 'w'], ['month', 'Month', 'm'], ['agenda', 'Agenda', 'a']] as const;
  type Index = FundCalendarIndex & { start: Date; end: Date; at: number };

  let anchor = $state(weekStart(new Date()));
  const firstMode = window.matchMedia?.('(max-width: 640px)').matches ? 'agenda' : 'week';
  let mode = $state<Mode>(firstMode);
  let dayMode = $state<'week' | 'agenda'>(firstMode);  // where Month hands back to
  let month = $state(monthStart(new Date()));
  let monthIndex = $state<Index | null>(null);
  let monthError = $state('');
  let pickerOpen = $state(false);
  let pickerMonth = $state(monthStart(new Date()));
  let pickerIndex = $state<Index | null>(null);
  let pickerFailed = $state(false);
  let pickerEl = $state<HTMLElement>();
  let pickedDay = $state<Date | null>(null);
  let data = $state<FundCalendar | null>(null);
  let loading = $state(true);
  let loadError = $state('');
  let scope = $state<'team' | 'me'>('team');
  let selectedId = $state<string | null>(null);
  // The call pane docks beside the calendar; expanded, it is the call page and the calendar is hidden.
  let expanded = $state(false);
  // Docking needs 250 sidebar + 72 page padding + 18 gap + 380 pane + 480 grid = 1200px.
  const smallQuery = window.matchMedia?.('(max-width: 1199px)');
  let smallScreen = $state(!!smallQuery?.matches);  // no room to dock: a call always opens as its page
  let returnScroll = 0;
  let now = $state(new Date());
  let noteBody = $state('');
  let noteTarget = $state('');
  let stepText = $state('');
  let stepDue = $state('');
  let addingNote = $state(false);
  let noteSaving = $state(false);
  let noteError = $state('');
  let pdfBusy = $state('');
  let pdfError = $state('');
  let closeButton = $state<HTMLButtonElement>();
  let pageEl = $state<HTMLElement>();
  let query = $state('');
  let searchOpen = $state(false);
  let searchEl = $state<HTMLElement>();
  let searchInput = $state<HTMLInputElement>();
  let index = $state<Index | null>(null);
  let indexError = $state('');
  const indexCache = new Map<string, { at: number; promise: Promise<Index> }>();
  let searchNotice = $state('');
  let opener: HTMLElement | null = null;
  let sequence = 0;
  let demoNotice = $state('');
  // Dev only: the sandbox has no calendar grants, so jojo-ui.test gets a synthetic week.
  const sandboxDemoAvailable = $derived(import.meta.env.DEV && $userEmail === 'ui@jojo-ui.test');

  const viewer = $derived(data?.viewer || monthIndex?.viewer || index?.viewer || '');
  const members = $derived(data?.members || monthIndex?.members || index?.members || []);
  const byPeople = <T extends { team: string[] }>(meeting: T) => scope === 'team' || meeting.team.includes(viewer);
  const colors = $derived(ownerColors(members, viewer));
  const agent = $derived(($agentName || 'Dealton').split(' ')[0]);
  const visible = $derived((data?.meetings || []).filter(byPeople));
  const groups = $derived(overlapGroups(visible, now));
  const overlapIds = $derived(new Set(groups.flat().map(meeting => meeting.id)));
  const rows = $derived(coordination(visible, now, groups));
  const hours = $derived(hourRange(visible));
  const days = $derived(Array.from({ length: 7 }, (_, i) => addDays(anchor, i))
    .filter((day, i) => i < 5 || visible.some(meeting => sameDay(new Date(meeting.start), day))));
  const selected = $derived(data?.meetings.find(meeting => meeting.id === selectedId) || null);
  const unread = $derived(members.filter(member => member.calendar !== 'connected'));
  const unreadText = $derived(unread.map(member =>
    `${member.email === viewer ? 'Your' : `${name(member.email)}’s`} calendar `
    + (member.calendar === 'not_connected' ? 'isn’t connected.' : 'couldn’t be read just now.')).join(' '));
  const thisWeek = $derived(sameDay(anchor, weekStart(now)));
  const RANGE = new Intl.DateTimeFormat([], { month: 'short', day: 'numeric', year: 'numeric' });
  const rangeLabel = $derived(mode === 'month' ? month.toLocaleDateString([], { month: 'long', year: 'numeric' })
    : RANGE.formatRange(anchor, days[days.length - 1]));
  const unit = $derived(mode === 'month' ? 'month' : 'week');  // what ‹ › step by
  const monthDays = $derived(monthGrid(month));
  const monthShown = $derived((monthIndex?.meetings || []).filter(byPeople));
  const monthByDay = $derived(monthShown.reduce((byDay, meeting) => {
    const key = localDay(new Date(meeting.start));
    byDay.set(key, [...(byDay.get(key) || []), meeting]);
    return byDay;
  }, new Map<string, FundCalendarIndexMeeting[]>()));
  const inMonth = $derived(monthShown.filter(meeting => new Date(meeting.start).getMonth() === month.getMonth()));
  const shownCount = $derived(mode === 'month' ? inMonth.length : visible.length);
  const lpShown = $derived(new Set((mode === 'month' ? inMonth : visible).flatMap(meeting => meeting.investors.map(lpName))).size);
  const pickerBusy = $derived(pickerIndex
    ? new Set(pickerIndex.meetings.filter(byPeople).map(meeting => localDay(new Date(meeting.start)))) : null);

  // Search runs over a 26-week index in the browser; the week grid stays the source of detail.
  const searchTerms = $derived(query.trim().toLowerCase().split(/\s+/).filter(Boolean));
  const matches = $derived(!index || !searchTerms.length ? [] : index.meetings.filter(meeting => {
    const text = [meeting.summary, ...meeting.team.map(name),
      ...meeting.investors.flatMap(person => [person.display_name, person.institution_name])].join(' ').toLowerCase();
    return searchTerms.every(term => text.includes(term));
  }));
  const upcomingMatches = $derived(matches.filter(meeting => Date.parse(meeting.end) > now.getTime()));
  const earlierMatches = $derived(matches.filter(meeting => Date.parse(meeting.end) <= now.getTime()).reverse());
  const searchRange = $derived(index ? RANGE.formatRange(index.start, addDays(index.end, -1)) : '');
  const searchGaps = $derived((index?.members || []).filter(member => member.calendar !== 'connected'));

  const TIME = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });
  const time = (iso: string) => TIME.format(new Date(iso));
  const span = (meeting: { start: string; end: string }) => TIME.formatRange(new Date(meeting.start), new Date(meeting.end));
  const dayName = (iso: string) => new Date(iso).toLocaleDateString([], { weekday: 'long' });
  const dayLabel = (date: Date) => date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const shortDate = (iso: string) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  const resultWhen = (iso: string) => new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric',
    ...(new Date(iso).getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) }) + ` · ${time(iso)}`;
  const noteLp = $derived(selected?.investors.find(person => person.counterparty_id === noteTarget) || selected?.investors[0]);
  const stepChanged = $derived(!!stepText.trim()
    && (stepText.trim() !== (noteLp?.next_step || '') || stepDue !== (noteLp?.follow_up_on || '')));
  const saveLabel = $derived(noteBody.trim() && stepChanged ? 'Save note and next step'
    : noteBody.trim() ? 'Save note' : stepChanged ? 'Save next step' : 'Save');
  // A follow-up date is a calendar day; noon keeps it on that day in every timezone.
  const dueLabel = (day: string) => new Date(`${day}T12:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });
  const setByLabel = (person: FundCalendarInvestor) => !person.next_step_set_by?.includes('@')
    ? 'Suggested from mail activity.'
    : `Set by ${name(person.next_step_set_by)}${person.next_step_set_at ? ` · ${shortDate(person.next_step_set_at)}` : ''}.`;
  function openLp(id: string) {
    selectedId = null;
    expanded = false;
    $currentView = id;
  }
  const hourLabel = (hour: number) => new Date(2000, 0, 1, hour).toLocaleTimeString([], { hour: 'numeric' });
  const name = (email: string) => email === viewer ? 'You'
    : (members.find(member => member.email === email)?.name || email.split('@')[0]);
  const hoursFrom = (iso: string, day: Date) => (Date.parse(iso) - day.getTime()) / 3_600_000;
  const stageOf = (meeting: FundCalendarMeeting) =>
    meeting.investors.map(person => person.stage).find(stage => FUND_STAGES.some(item => item.key === stage))
      ?? (meeting.investors.length ? 'Prospect' : undefined);  // an LP with no stage is a Prospect
  const meetingsOn = (day: Date) => visible.filter(meeting => sameDay(new Date(meeting.start), day));

  function blockStyle(meeting: FundCalendarMeeting, day: Date, lane: number, lanes: number) {
    const top = (hoursFrom(meeting.start, day) - hours[0]) * HOUR_PX;
    const bottom = (Math.min(hoursFrom(meeting.end, day), hours[1]) - hours[0]) * HOUR_PX;
    return `top:${top}px;height:${Math.max(bottom - top - 3, 20)}px;`
      + `left:calc(${lane} * 100% / ${lanes} + 3px);width:calc(100% / ${lanes} - 6px);`
      + `--owner:${colors[meeting.lead] || 'var(--border-strong)'}`;
  }

  function rowText(row: CoordinationRow): string {
    const lp = meetingName(row.meeting);
    if (row.kind === 'overlap')
      return `${lp} has ${row.group.length} meetings ${thisWeek ? 'this' : 'that'} week with different leads: `
        + row.group.map(meeting => `${name(meeting.lead)} on ${dayName(meeting.start)}`).join(', ')
        + '. Agree one answer before either call.';
    if (row.kind === 'declined') return `${lp} declined the invite for ${dayName(row.meeting.start)}.`;
    if (row.kind === 'unconfirmed') return `${lp} on ${dayName(row.meeting.start)} hasn't accepted the invite yet.`;
    return `${lp} on ${dayName(row.meeting.start)} has no notes logged.`;
  }
  const ROW_WORD = { overlap: '⚠ Overlap', declined: '✕ Declined', unconfirmed: '◌ Unconfirmed', nonotes: '✎ No notes' };

  async function sandboxDemo(result: FundCalendar | FundCalendarIndex, start: Date, end: Date)
    : Promise<{ calendar: FundCalendar; notice: string } | null> {
    // Keep the import inside this direct compile-time guard: Vite must omit the fixture from builds.
    if (import.meta.env.DEV) {
      const { sandboxDemoCalendar, sandboxDemoNotice } = await import('../lib/calendar/sandbox-demo');
      const you = result.members.find(member => member.email === result.viewer)?.name || 'You';
      return { calendar: sandboxDemoCalendar(result.viewer, you, $fundLps, start, end),
        notice: sandboxDemoNotice };
    }
    return null;
  }

  async function load() {
    const current = ++sequence;
    loading = true;
    try {
      const start = anchor, result = await getFundCalendar(anchor.toISOString(), addDays(anchor, 7).toISOString());
      const demo = sandboxDemoAvailable && !result.meetings.length ? await sandboxDemo(result, start, addDays(start, 7)) : null;
      if (current !== sequence) return;
      data = demo?.calendar || result;
      demoNotice = demo?.notice || '';
      loadError = '';
    } catch (err: any) {
      if (current === sequence) loadError = err?.message || 'Could not read the team calendar.';
    } finally {
      if (current === sequence) loading = false;
    }
  }

  function goWeek(week: Date) {
    anchor = week;
    data = null;
    selectedId = null;
    expanded = false;
    return load();
  }

  function goMonth(first: Date) {
    month = first;
    monthIndex = null;
    monthError = '';
    const days = monthGrid(first);
    readIndex(days[0], addDays(days[days.length - 1], 1)).then(
      result => { if (sameDay(month, first)) monthIndex = result; },
      (err: any) => { if (sameDay(month, first)) monthError = err?.message || 'Could not read the team calendar.'; });
  }

  /** One step back (-1) or forward (1) in the current view; 0 is Today. */
  function go(step: number) {
    pickedDay = null;
    if (mode === 'month') goMonth(step ? addMonths(month, step) : monthStart(new Date()));
    else goWeek(step ? addDays(anchor, 7 * step) : weekStart(new Date()));
  }

  function setMode(next: Mode) {
    if (next === mode) return;
    const from = mode;
    mode = next;
    pickedDay = null;
    if (next === 'month') {
      goMonth(monthStart(thisWeek ? now : anchor));
      return;
    }
    dayMode = next;
    if (from !== 'month') return;
    // Leaving Month lands on the week holding today, else the month's first day, as Google does.
    const week = weekStart(sameDay(monthStart(now), month) ? now : month);
    if (!data || !sameDay(week, anchor)) goWeek(week);
  }

  /** Week or Agenda for that day, with its column marked and brought into view. */
  async function goToDay(day: Date) {
    if (mode === 'month') mode = dayMode;
    pickedDay = day;
    if (!data || !sameDay(weekStart(day), anchor)) await goWeek(weekStart(day));
    await tick();
    [...pageEl?.querySelectorAll<HTMLElement>('[data-day]') || []].find(node => node.dataset.day === localDay(day))
      ?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }

  /** Names and times for a window, shared by search, Month and the date picker for INDEX_TTL. */
  function readIndex(start: Date, end: Date): Promise<Index> {
    const key = `${start.getTime()}-${end.getTime()}`;
    const hit = indexCache.get(key);
    if (hit && Date.now() - hit.at < INDEX_TTL) return hit.promise;
    const promise = (async () => {
      const result = await getFundCalendarIndex(start.toISOString(), end.toISOString());
      const demo = sandboxDemoAvailable && !result.meetings.length ? await sandboxDemo(result, start, end) : null;
      return { ...(demo?.calendar || result), start, end, at: Date.now() };
    })();
    indexCache.set(key, { at: Date.now(), promise });
    promise.catch(() => indexCache.delete(key));
    return promise;
  }

  function togglePicker(open = !pickerOpen) {
    pickerOpen = open;
    if (open) showPickerMonth(mode === 'month' ? month : monthStart(thisWeek ? now : anchor));
  }

  function showPickerMonth(first: Date) {
    pickerMonth = first;
    pickerIndex = null;
    pickerFailed = false;
    const days = monthGrid(first);
    readIndex(days[0], addDays(days[days.length - 1], 1)).then(
      result => { if (sameDay(pickerMonth, first)) pickerIndex = result; },
      () => { if (sameDay(pickerMonth, first)) pickerFailed = true; });
  }

  function pickDay(day: Date) {
    pickerOpen = false;
    if (mode === 'month') goMonth(monthStart(day));
    else goToDay(day);
  }

  const isPicked = (day: Date) => mode === 'month' ? sameDay(monthStart(day), month) : sameDay(weekStart(day), anchor);

  // Google Calendar's keys. Never while typing, with a modifier, or while a call is open.
  const SHORTCUTS: Record<string, () => void> = {
    t: () => go(0), j: () => go(1), n: () => go(1), k: () => go(-1), p: () => go(-1),
    w: () => setMode('week'), m: () => setMode('month'), a: () => setMode('agenda'),
    g: () => togglePicker(true), '/': () => searchInput?.focus(),
  };

  function windowKeys(event: KeyboardEvent) {
    const target = event.target;
    const typing = target instanceof Element && !!target.closest('input, textarea, select, [contenteditable="true"]');
    if (event.key === 'Escape') {
      if (selectedId && typing) return;  // a half-written note is never dropped by Escape
      if (selectedId && expanded && !smallScreen) setExpanded(false);
      else if (selectedId) closeMeeting();
      else if (pickerOpen) pickerOpen = false;
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey || selectedId || typing) return;
    const run = SHORTCUTS[event.key];
    if (!run) return;
    event.preventDefault();
    run();
  }

  function windowClick(event: MouseEvent) {
    const target = event.target as Node;
    if (searchOpen && !searchEl?.contains(target)) searchOpen = false;
    if (pickerOpen && !pickerEl?.contains(target)) pickerOpen = false;
  }

  function setScope(next: 'team' | 'me') {
    scope = next;
    if (selected && !byPeople(selected)) closeMeeting();
  }

  /** Prefill the next-step field with the LP's current one, so saving it unchanged writes nothing. */
  function pickNoteLp(id: string) {
    noteTarget = id;
    const person = selected?.investors.find(item => item.counterparty_id === id);
    stepText = person?.next_step || '';
    stepDue = person?.follow_up_on || '';
  }

  /** `from` gets focus back on close; capture it before any await, when currentTarget is still set. */
  async function openMeeting(id: string, from: EventTarget | null) {
    opener = from as HTMLElement | null;
    selectedId = id;
    noteBody = '';
    noteError = '';
    pdfError = '';
    addingNote = false;
    if (smallScreen && !expanded) setExpanded(true);
    pickNoteLp(data?.meetings.find(meeting => meeting.id === id)?.investors[0]?.counterparty_id || '');
    await tick();
    closeButton?.focus({ preventScroll: true });  // a scroll here would cancel showMeeting's
  }

  /** The call page hides the calendar; coming back returns to where the calendar was scrolled. */
  async function setExpanded(next: boolean) {
    const scroller = pageEl?.closest('.main');
    if (next && !expanded) returnScroll = scroller?.scrollTop || 0;
    expanded = next;
    await tick();
    scroller?.scrollTo?.({ top: next ? 0 : returnScroll });
  }

  /** A strip row, search result or month chip acts as a click on its meeting, after bringing it into view.
   *  With no `from`, closing the drawer returns focus to the meeting itself. */
  function showMeeting(id: string, from: EventTarget | null) {
    const block = [...pageEl?.querySelectorAll<HTMLElement>('[data-meeting]') || []].find(node => node.dataset.meeting === id);
    block?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
    return openMeeting(id, from || block || null);
  }

  async function openSearch() {
    searchOpen = true;
    searchNotice = '';
    if (index && Date.now() - index.at < INDEX_TTL) return;
    indexError = '';
    const start = addDays(weekStart(new Date()), -7 * SEARCH_WEEKS_BACK);
    try {
      index = await readIndex(start, addDays(start, 7 * 26));
    } catch (err: any) {
      indexError = err?.message || 'Could not search the team calendar.';
    }
  }

  /** Go to the result's week, then open it exactly as a click on the grid would. */
  async function jumpTo(meeting: FundCalendarIndexMeeting) {
    searchOpen = false;
    if (mode === 'month') mode = dayMode;
    pickedDay = null;
    if (!byPeople(meeting)) scope = 'team';
    const week = weekStart(new Date(meeting.start));
    if (!data || !sameDay(week, anchor)) await goWeek(week);
    await tick();
    if (data?.meetings.some(item => item.id === meeting.id)) await showMeeting(meeting.id, null);
    else if (data) {
      searchNotice = 'That meeting is no longer on the team’s calendars.';
      index = null;
      indexCache.clear();
    }
  }

  /** Enter opens the first result, arrows walk the list, Escape closes it and keeps focus in the field. */
  function searchKeys(event: KeyboardEvent) {
    const results = [...(searchEl?.querySelectorAll<HTMLButtonElement>('.cal-res') || [])];
    if (event.key === 'Escape' && searchOpen) {
      event.stopPropagation();
      searchInput?.focus();  // before closing: focusing the field reopens the list
      searchOpen = false;
    } else if (event.key === 'Enter' && event.currentTarget === searchInput) {
      const first = upcomingMatches[0] || earlierMatches[0];
      if (first) { event.preventDefault(); jumpTo(first); }
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && results.length) {
      event.preventDefault();
      const next = results.indexOf(event.currentTarget as HTMLButtonElement) + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) searchInput?.focus();
      else results[Math.min(next, results.length - 1)].focus();
    }
  }

  async function closeMeeting() {
    const wasExpanded = expanded;
    selectedId = null;
    expanded = false;
    if (wasExpanded) {
      await tick();
      pageEl?.closest('.main')?.scrollTo?.({ top: returnScroll });
    }
    opener?.focus({ preventScroll: wasExpanded });
    opener = null;
  }

  /** A team note on the LP and, when it changed, the LP's next step: the records the LP page already keeps. */
  async function saveAfterCall(meeting: FundCalendarMeeting) {
    const body = noteBody.trim(), step = stepText.trim(), due = step ? stepDue || null : null;
    const writeStep = stepChanged;
    if ((!body && !writeStep) || !noteTarget) return;
    if (demoNotice && data) {  // Never write: a real note would trigger a billed briefing refresh.
      const written = new Date().toISOString();
      const note = { note_id: `sandbox-demo-${written}`, counterparty_id: noteTarget, title: 'Meeting notes',
        body, author_email: viewer, created_at: written, updated_at: written };
      data = { ...data, meetings: data.meetings.map(item => item.id !== meeting.id ? item : {
        ...item, notes: body ? [note, ...item.notes] : item.notes,
        investors: item.investors.map(person => !writeStep || person.counterparty_id !== noteTarget ? person
          : { ...person, next_step: step, follow_up_on: due, next_step_set_by: viewer, next_step_set_at: written }),
      }) };
      noteBody = '';
      addingNote = false;
      return;
    }
    noteSaving = true;
    noteError = '';
    try {
      if (body) {
        await addFundNote(noteTarget, `Meeting notes · ${dayLabel(new Date(meeting.start))}`, body);
        noteBody = '';  // saved: a retry after a failed next step must not post it twice
      }
      if (writeStep) await setFundFollowUp(noteTarget, step, due);
      addingNote = false;
      await load();
      pickNoteLp(noteTarget);
    } catch (err: any) {
      noteError = err?.message || 'Could not save.';
    } finally {
      noteSaving = false;
    }
  }

  async function downloadPdf(meeting: FundCalendarMeeting, counterpartyId: string) {
    if (!meeting.own_event_id) return;
    pdfBusy = counterpartyId;
    pdfError = '';
    try {
      await downloadFundCallBriefPdf(meeting.own_event_id, counterpartyId);
    } catch (err: any) {
      pdfError = err?.message || 'Could not download the call brief.';
    } finally {
      pdfBusy = '';
    }
  }

  onMount(() => {
    load();
    const timer = setInterval(() => now = new Date(), 60_000);
    const resize = () => {
      smallScreen = !!smallQuery?.matches;
      if (smallScreen && selectedId) expanded = true;
    };
    smallQuery?.addEventListener?.('change', resize);
    return () => { clearInterval(timer); smallQuery?.removeEventListener?.('change', resize); };
  });
</script>

<svelte:window onkeydown={windowKeys} onclick={windowClick} />

{#snippet avatar(email: string, small = false)}
  <span class="cal-av" class:sm={small} style:background={colors[email] || 'var(--border-strong)'}
    title={name(email)} aria-hidden="true">{initials(email, members)}</span>
{/snippet}

{#snippet resultGroup(label: string, list: FundCalendarIndexMeeting[])}
  {#if list.length}
    <p class="cal-res-h">{label} · {list.length}</p>
    {#each list.slice(0, SEARCH_SHOWN) as meeting (meeting.id)}
      <button class="cal-res" onclick={() => jumpTo(meeting)} onkeydown={searchKeys}>
        <span class="cal-res-when">{resultWhen(meeting.start)}</span>
        <span class="cal-res-main"><strong>{meetingName(meeting)}</strong><small>{meeting.summary}</small></span>
        <span class="cal-res-lead">{@render avatar(meeting.lead, true)}{name(meeting.lead)}</span>
      </button>
    {/each}
    {#if list.length > SEARCH_SHOWN}<p class="cal-res-note">{list.length - SEARCH_SHOWN} more — add a word to narrow it.</p>{/if}
  {/if}
{/snippet}

{#snippet monthView()}
  <div class="cal-frame" aria-busy={!monthIndex}>
  <div class="cal-month">
    <div class="cal-mhead" aria-hidden="true">{#each ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as weekday (weekday)}<span>{weekday}</span>{/each}</div>
    <div class="cal-mbody">
      {#each monthDays as day (day.getTime())}
        {@const list = monthByDay.get(localDay(day)) || []}
        {@const today = sameDay(day, now)}
        <div class="cal-mday" class:out={day.getMonth() !== month.getMonth()} class:past={!today && day.getTime() < now.getTime()}>
          <button class="cal-mnum" class:today aria-current={today ? 'date' : undefined}
            aria-label="{dayLabel(day)}{list.length ? `, ${list.length} LP meeting${list.length === 1 ? '' : 's'}` : ''} — open in {dayMode === 'week' ? 'Week' : 'Agenda'}"
            onclick={() => goToDay(day)}>{day.getDate()}</button>
          {#each list.slice(0, MONTH_CHIPS) as meeting (meeting.id)}
            <button class="cal-mchip" class:past={isPast(meeting, now)} style:--owner={colors[meeting.lead] || 'var(--border-strong)'}
              title="{span(meeting)} · {meeting.summary} · {name(meeting.lead)}" onclick={() => jumpTo(meeting)}>
              <span class="cal-mchip-t">{time(meeting.start)}</span> {meetingName(meeting)}</button>
          {/each}
          {#if list.length > MONTH_CHIPS}<button class="cal-mmore" onclick={() => goToDay(day)}>+{list.length - MONTH_CHIPS} more</button>{/if}
        </div>
      {/each}
    </div>
  </div>
  {#if !monthIndex}{@render loadingNote()}{/if}
  </div>
  {#if monthIndex && !inMonth.length}<p class="cal-empty">No LP meetings on {scope === 'me' ? 'your' : 'the team’s'} calendars in {rangeLabel}.</p>{/if}
{/snippet}

{#snippet loadingNote()}
  <!-- Over the empty grid of the range being read, so the page keeps its shape while it loads. -->
  <div class="cal-loading" role="status"><span class="spinner"></span>Reading the team’s calendars…</div>
{/snippet}

{#snippet viewIcon(key: Mode)}
  <svg class="cal-i" viewBox="0 0 24 24" aria-hidden="true">
    {#if key === 'agenda'}
      <path d="M9 7h11M9 12h11M9 17h11" />
      {#each [7, 12, 17] as y (y)}<circle cx="5" cy={y} r=".9" fill="currentColor" />{/each}
    {:else}
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path stroke-width="1.2" d={key === 'week' ? 'M7 4.5v15M10.5 4.5v15M14 4.5v15M17.5 4.5v15'
        : 'M3.5 9.5h17M3.5 14.5h17M9.2 4.5v15M14.8 4.5v15'} />
    {/if}
  </svg>
{/snippet}

{#snippet statusWord(meeting: FundCalendarMeeting)}
  {@const info = meetingStatus(meeting, now, overlapIds)}
  <span class="cal-status {info.tone}">{#if info.spark}<span class="cal-spark">✦</span>{' '}{/if}{info.word}</span>
{/snippet}

<div class="cal-shell" class:docked={!!selected && !expanded} class:expanded={!!selected && expanded}>
<div class="cal-page" bind:this={pageEl}>
  <div class="cal-bar">
    <h1 class="page-title">Calendar</h1>
    <div class="cal-nav">
      <button class="today-mini-btn" title="Today (t)" onclick={() => go(0)}>Today</button>
      <button class="cal-chev" aria-label="Previous {unit}" title="Previous {unit} (k)" onclick={() => go(-1)}>
        <svg class="cal-i" viewBox="0 0 24 24" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg></button>
      <button class="cal-chev" aria-label="Next {unit}" title="Next {unit} (j)" onclick={() => go(1)}>
        <svg class="cal-i" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg></button>
      <div class="cal-picker" bind:this={pickerEl}>
        <button class="cal-range" aria-haspopup="dialog" aria-expanded={pickerOpen} title="Go to date (g)"
          onclick={() => togglePicker()}>{rangeLabel}<svg class="cal-i" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg></button>
        {#if pickerOpen}
          <div class="cal-picker-pop">
            <MiniMonth month={pickerMonth} today={now} busy={pickerBusy} failed={pickerFailed}
              isSelected={isPicked} onpick={pickDay} onmonth={(delta) => showPickerMonth(addMonths(pickerMonth, delta))} />
          </div>
        {/if}
      </div>
    </div>
    {#if mode === 'month' ? monthIndex : data}<span class="cal-count">{shownCount} meeting{shownCount === 1 ? '' : 's'} · {lpShown} LP{lpShown === 1 ? '' : 's'}</span>{/if}
    {#if loading && data}<span class="cal-count" role="status">Refreshing…</span>{/if}
    {#if searchNotice}<span class="cal-count" role="status">{searchNotice}</span>{/if}
    <div class="cal-seg" role="group" aria-label="View">
      {#each VIEWS as [key, label, hotkey] (key)}
        <button class:on={mode === key} aria-pressed={mode === key} aria-label={label} data-tip="{label} ({hotkey})"
          onclick={() => setMode(key)}>{@render viewIcon(key)}{#if mode === key}{label}{/if}</button>
      {/each}
    </div>
  </div>

  <div class="cal-filters">
    {#if members.length > 1}
      <div class="cal-people">
        <div class="seg" role="group" aria-label="Whose meetings">
          <button class:on={scope === 'team'} aria-pressed={scope === 'team'} onclick={() => setScope('team')}>Team</button>
          <button class:on={scope === 'me'} aria-pressed={scope === 'me'} onclick={() => setScope('me')}>Only me</button>
        </div>
        {#if scope === 'team'}
          <!-- The lead's colour key; not a control. -->
          <span class="cal-legend">{#each members as member (member.email)}<span class="cal-who">{@render avatar(member.email, true)}{name(member.email)}</span>{/each}</span>
        {/if}
      </div>
    {/if}
    <div class="cal-search" bind:this={searchEl} role="search">
      <input type="search" bind:this={searchInput} bind:value={query} placeholder="Search LP meetings…"
        aria-label="Search LP meetings by LP, person or title" onfocus={openSearch} oninput={openSearch}
        onkeydown={searchKeys} />
      {#if searchOpen && searchTerms.length}
        <div class="cal-results">
          {#if !index}
            <p class="cal-res-note" role={indexError ? 'alert' : 'status'}>{indexError || 'Searching the team’s calendars…'}</p>
          {:else}
            {#if !matches.length}<p class="cal-res-note">No LP meetings match “{query.trim()}”.</p>{/if}
            {@render resultGroup('Upcoming', upcomingMatches)}
            {@render resultGroup('Earlier', earlierMatches)}
            <p class="cal-res-foot">Searches {searchRange}.{#each searchGaps as member (member.email)}{' '}{member.email === viewer ? 'Your' : `${name(member.email)}’s`} calendar isn’t included.{/each}</p>
          {/if}
        </div>
      {/if}
    </div>
  </div>

  {#if demoNotice}<p class="cal-demo" role="note">{demoNotice}</p>{/if}

  {#if unread.length}
    <p class="cal-unread">
      {unreadText} Their LP meetings can’t appear here.
      {#if unread.some(member => member.email === viewer && member.calendar === 'not_connected')}
        <button class="cal-link" onclick={() => $currentView = 'settings'}>Connect in Settings →</button>
      {/if}
    </p>
  {/if}

  {#if mode === 'month'}
    {#if monthError}
      <div class="today-empty" role="alert">The team calendar is unavailable. {monthError} <button class="today-mini-btn" onclick={() => goMonth(month)}>Retry</button></div>
    {:else}
      {@render monthView()}
    {/if}
  {:else if loadError}
    <div class="today-empty" role="alert">The team calendar is unavailable. {loadError} <button class="today-mini-btn" onclick={load}>Retry</button></div>
  {:else if !data && mode === 'agenda'}
    <div class="cal-frame cal-frame-empty" aria-busy="true">{@render loadingNote()}</div>
  {:else}
    {#if data && rows.length}
      <section class="cal-coord" aria-label="Coordination, {thisWeek ? 'this week' : rangeLabel}">
        <div class="cal-coord-h">{thisWeek ? 'This week' : rangeLabel}</div>
        {#each rows.slice(0, 6) as row (row.kind === 'overlap' ? `overlap:${overlapKey(row.group)}` : `${row.kind}:${row.meeting.id}`)}
          <button class="cal-coord-row" onclick={(event) => showMeeting(row.meeting.id, event.currentTarget)}>
            <span class="cal-word" class:red={row.kind === 'declined'}>{ROW_WORD[row.kind]}</span>
            <span class="cal-coord-text">{rowText(row)}</span>
            <span class="cal-link" aria-hidden="true">Show</span>
          </button>
        {/each}
        {#if rows.length > 6}<div class="cal-coord-more">{rows.length - 6} more in the {mode === 'week' ? 'grid' : 'list'} below.</div>{/if}
      </section>
    {/if}

    {#if mode === 'week'}
      <div class="cal-frame" aria-busy={!data}>
      <div class="cal-scroll">
        <div class="cal-grid" style:--days={days.length}>
          <div class="cal-head">
            <div></div>
            {#each days as day (day.getTime())}
              {@const today = sameDay(day, now)}
              <div class="cal-dh" class:today class:picked={!!pickedDay && sameDay(day, pickedDay)}
                class:past={day.getTime() < now.getTime() && !today} data-day={localDay(day)}>
                {day.toLocaleDateString([], { weekday: 'short' })}{#if today}<span class="cal-tlabel">Today</span>{/if}
                <b>{day.getDate()}</b>
              </div>
            {/each}
          </div>
          <div class="cal-body">
            <div class="cal-gutter">
              {#each { length: hours[1] - hours[0] } as _, i}<div class="cal-hour">{i ? hourLabel(hours[0] + i) : ''}</div>{/each}
            </div>
            {#each days as day (day.getTime())}
              {@const today = sameDay(day, now)}
              <div class="cal-col" class:past={day.getTime() < now.getTime() && !today} style:height="{(hours[1] - hours[0]) * HOUR_PX}px">
                {#each layoutDay(meetingsOn(day)) as { item: meeting, lane, lanes } (meeting.id)}
                  {@const height = (Date.parse(meeting.end) - Date.parse(meeting.start)) / 3_600_000 * HOUR_PX}
                  {@const narrow = lanes > 1}
                  <!-- Beside the call pane the columns are slim: like overlapping lanes, drop the time and avatars. -->
                  {@const slim = narrow || (!!selected && !expanded)}
                  <button class="cal-ev" class:narrow={slim} class:mine={meeting.team.includes(viewer)} class:past={isPast(meeting, now)}
                    class:sel={selectedId === meeting.id} data-meeting={meeting.id} style={blockStyle(meeting, day, lane, lanes)}
                    aria-label="{time(meeting.start)} {meetingName(meeting)}, led by {name(meeting.lead)}, {meetingStatus(meeting, now, overlapIds).word}"
                    onclick={(event) => openMeeting(meeting.id, event.currentTarget)}>
                    {#if height >= 52 && !narrow}
                      <span class="cal-ev-t">{slim ? time(meeting.start) : span(meeting)}</span>
                      <span class="cal-ev-n">{meetingName(meeting)}</span>
                    {:else}
                      <!-- Narrow lanes drop the time: the block's position already says it. -->
                      <span class="cal-ev-n">{#if !slim}<span class="cal-ev-t">{time(meeting.start)}</span>{' '}{/if}{meetingName(meeting)}</span>
                    {/if}
                    {#if height >= 36}<span class="cal-ev-s">{@render statusWord(meeting)}</span>{/if}
                    {#if !slim}<span class="cal-ev-avs">{#each meeting.team as email (email)}{@render avatar(email, true)}{/each}</span>{/if}
                  </button>
                {/each}
                {#if today && hoursFrom(now.toISOString(), day) >= hours[0] && hoursFrom(now.toISOString(), day) <= hours[1]}
                  <div class="cal-now" style:top="{(hoursFrom(now.toISOString(), day) - hours[0]) * HOUR_PX}px"><span>{time(now.toISOString())}</span></div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      </div>
      {#if !data}{@render loadingNote()}{/if}
      </div>
      {#if data && !visible.length}<p class="cal-empty">No LP meetings on {scope === 'me' ? 'your' : 'the team’s'} calendars this week.</p>{/if}
    {:else}
      <div class="cal-agenda">
        {#each days as day (day.getTime())}
          {@const list = meetingsOn(day).sort((a, b) => Date.parse(a.start) - Date.parse(b.start))}
          <section class="cal-ag-day" class:picked={!!pickedDay && sameDay(day, pickedDay)} data-day={localDay(day)}>
            <header class="cal-ag-dh">
              <h3>{dayLabel(day)}</h3>
              {#if sameDay(day, now)}<span class="status-pill status-pill-grey">Today</span>{/if}
              <span class="cal-count">{list.length} meeting{list.length === 1 ? '' : 's'}</span>
            </header>
            {#each list as meeting (meeting.id)}
              {@const stage = stageOf(meeting)}
              <button class="cal-ag-row" class:past={isPast(meeting, now)} class:sel={selectedId === meeting.id}
                data-meeting={meeting.id} style:--owner={colors[meeting.lead] || 'var(--border-strong)'}
                onclick={(event) => openMeeting(meeting.id, event.currentTarget)}>
                <span class="cal-ag-t">{span(meeting)}</span>
                <span class="cal-ag-n"><strong>{meetingName(meeting)}</strong>{#if stage}<span class="status-pill status-pill-grey">{fundStageLabel(stage)}</span>{/if}<small>{meeting.summary}</small></span>
                {@render statusWord(meeting)}
                <span class="cal-ag-avs">{#each meeting.team as email (email)}{@render avatar(email)}{/each}</span>
              </button>
            {:else}
              <p class="cal-count cal-ag-none">No LP meetings.</p>
            {/each}
          </section>
        {/each}
      </div>
    {/if}
  {/if}
</div>

{#snippet providerLink(url: string, label: string)}
  <a class="cal-link" href={url} target="_blank" rel="noopener noreferrer">{label} ↗</a>
{/snippet}

{#snippet sourceList(view: CallBriefView)}
  {#if view.cited.length}
    <ol class="cal-srcs">
      {#each view.cited as source (source.id)}
        <li>{#if safeUrl(source.url)}<a href={safeUrl(source.url)} target="_blank" rel="noopener noreferrer">{source.label}</a>{:else}{source.label}{/if}</li>
      {/each}
    </ol>
  {/if}
  {#if view.uncited}<p class="cal-meta">{view.uncited} more from the LP brief, on the LP page.</p>{/if}
{/snippet}

{#snippet brief(meeting: FundCalendarMeeting, person: FundCalendarInvestor, wide: boolean)}
  <section class="cal-brief">
    <p class="cal-lbl"><span class="cal-spark">✦</span> Call brief{meeting.investors.length > 1 ? ` · ${person.display_name}` : ''}</p>
    {#if person.call_brief}
      {@const view = callBriefView(person.call_brief.content, person.call_brief.sources)}
      {#if wide}
        {#each view.sections as section, i (i)}
          <div class="cal-bsec">
            {#if section.title}<h3><span class="cal-bnum">{section.number.padStart(2, '0')}</span>{section.title}</h3>{/if}
            <div class="md-content cal-bbody">{@html section.html}</div>
          </div>
        {/each}
      {:else if view.sections.some(section => section.label)}
        <dl class="cal-bgrid">
          {#each view.sections as section, i (i)}<dt>{section.label}</dt><dd class="md-content">{@html section.html}</dd>{/each}
        </dl>
      {:else}
        {#each view.sections as section, i (i)}<div class="md-content">{@html section.html}</div>{/each}
      {/if}
      <div class="cal-meta cal-brief-meta">
        <span>Prepared by {agent} for {name(person.call_brief.prepared_for)} · {shortDate(person.call_brief.generated_at)} · review before relying on it.</span>
        {#if person.call_brief.prepared_for === viewer && meeting.own_event_id}
          <button class="today-mini-btn" onclick={() => downloadPdf(meeting, person.counterparty_id)}
            disabled={pdfBusy === person.counterparty_id}>{pdfBusy === person.counterparty_id ? 'Preparing…' : 'Download PDF'}</button>
        {/if}
      </div>
      {#if pdfError}<p class="cal-error" role="alert">{pdfError}</p>{/if}
      {#if !wide && person.call_brief.sources.length}
        <details class="cal-sources"><summary>{person.call_brief.sources.length} source{person.call_brief.sources.length === 1 ? '' : 's'}</summary>
          {@render sourceList(view)}
        </details>
      {/if}
    {:else if person.call_brief_status === 'generating'}
      <p class="cal-muted">{agent} is preparing this brief.</p>
    {:else if person.call_brief_status === 'failed'}
      <p class="cal-muted">This brief couldn’t be prepared. {agent} will try again.</p>
    {:else if person.call_brief_status === 'ready'}
      <p class="cal-muted">Brief ready · hidden in support view.</p>
    {:else}
      <p class="cal-muted">No call brief yet.</p>
    {/if}
  </section>
{/snippet}

{#snippet noteForm(meeting: FundCalendarMeeting)}
  {#if meeting.investors.length > 1}
    <select class="cal-select" value={noteTarget} onchange={(event) => pickNoteLp(event.currentTarget.value)} aria-label="Log the note on">
      {#each meeting.investors as person (person.counterparty_id)}<option value={person.counterparty_id}>{person.display_name}</option>{/each}
    </select>
  {/if}
  <textarea class="cal-textarea" rows="4" maxlength="10000" bind:value={noteBody} aria-label="Meeting notes"
    placeholder="What was said, what we promised, and what happens next."></textarea>
  <label class="cal-step-l" for="cal-step">Next step on {noteLp ? lpName(noteLp) : 'the LP'} <span>· optional</span></label>
  <div class="cal-step">
    <input id="cal-step" class="cal-input" maxlength="500" bind:value={stepText} placeholder="e.g. Send the Q3 letter" />
    <input class="cal-input cal-date" type="date" bind:value={stepDue} aria-label="Follow up on" disabled={!stepText.trim()} />
  </div>
  <p class="cal-meta">One next step per LP{noteLp?.next_step ? ', so this replaces the current one' : ''}. It shows on Today when it’s due.</p>
  {#if noteError}<p class="cal-error" role="alert">{noteError}</p>{/if}
  <div class="cal-actions">
    <button class="btn-primary" onclick={() => saveAfterCall(meeting)}
      disabled={noteSaving || (!noteBody.trim() && !stepChanged)}>{noteSaving ? 'Saving…' : saveLabel}</button>
    {#if addingNote}<button class="today-mini-btn" onclick={() => { addingNote = false; noteBody = ''; pickNoteLp(noteTarget); }}>Cancel</button>{/if}
  </div>
{/snippet}

{#snippet afterCall(meeting: FundCalendarMeeting)}
  <!-- Read-only support view cannot write notes; Use as can (the server enforces both). -->
  {@const canWrite = !isSupportView() || canUseAs()}
  {#if !isLogged(meeting)}
    <div class="cal-job">
      <div class="cal-job-h"><span class="cal-word">✎ No notes</span><strong>How did it go?</strong></div>
      <p>Notes go on the LP page, where the team and the next brief see them.</p>
      {#if canWrite}{@render noteForm(meeting)}{/if}
    </div>
  {:else}
    <p class="cal-lbl">Notes since the meeting</p>
    {#each meeting.notes as note (note.note_id)}
      <article class="cal-note">
        <div class="cal-note-h"><strong>{note.title}</strong><span>{name(note.author_email)} · {shortDate(note.created_at)}</span></div>
        <p>{note.body}</p>
      </article>
    {:else}
      <p class="cal-muted">No notes, only a next step.</p>
    {/each}
    {#each meeting.investors.filter(person => person.next_step) as person (person.counterparty_id)}
      <p class="cal-lbl cal-gap">Next step{meeting.investors.length > 1 ? ` · ${person.display_name}` : ''}</p>
      <div class="cal-next"><span>{person.next_step}</span>{#if person.follow_up_on}<span class="cal-due">Follow up {dueLabel(person.follow_up_on)}</span>{/if}</div>
      <p class="cal-meta">{setByLabel(person)}</p>
    {/each}
    {#if canWrite}
      {#if addingNote}
        <p class="cal-lbl cal-gap">Add a note</p>
        {@render noteForm(meeting)}
      {:else}
        <button class="today-mini-btn cal-gap" onclick={() => { addingNote = true; noteError = ''; }}>Add a note</button>
      {/if}
    {/if}
  {/if}
{/snippet}

{#snippet callBody(meeting: FundCalendarMeeting, wide: boolean)}
  {@const info = meetingStatus(meeting, now, overlapIds)}
  {@const row = rows.find(item => item.meeting.id === meeting.id || (item.kind === 'overlap' && item.group.some(other => other.id === meeting.id)))}
  {@const openUrl = safeUrl(meeting.open_url)}
  {#if isPast(meeting, now)}
    {@render afterCall(meeting)}
  {:else}
    {#if row && info.tone}
      <div class="cal-job" class:red={info.tone === 'red'}>
        <div class="cal-job-h"><span class="cal-word" class:red={info.tone === 'red'}>{info.word}</span></div>
        <p>{rowText(row)}</p>
        {#if openUrl && row.kind !== 'overlap'}{@render providerLink(openUrl, `Open in ${providerName(openUrl)}`)}{/if}
      </div>
    {/if}
    {#each meeting.investors as person (person.counterparty_id)}{@render brief(meeting, person, wide)}{/each}
  {/if}
{/snippet}

{#snippet callSide(meeting: FundCalendarMeeting)}
  {#if !isPast(meeting, now)}
    {#each meeting.investors as person (person.counterparty_id)}
      {#if person.call_brief}
        <div>
          <p class="cal-lbl">Call-specific evidence{meeting.investors.length > 1 ? ` · ${person.display_name}` : ''}</p>
          {@render sourceList(callBriefView(person.call_brief.content, person.call_brief.sources))}
        </div>
      {/if}
    {/each}
  {/if}
  {#each meeting.investors as person (person.counterparty_id)}
    {@const stage = FUND_STAGES.some(item => item.key === person.stage) ? person.stage! : 'Prospect'}
    <div class="cal-side-card">
      <p class="cal-lbl">{meeting.investors.length > 1 ? lpName(person) : 'On the LP page'}</p>
      <span>Stage: <b>{fundStageLabel(stage)}</b></span>
      <span>{#if person.next_step}Next step: <b>{person.next_step}</b>{#if person.follow_up_on}{' '}· follow up {dueLabel(person.follow_up_on)}{/if}{:else}No next step yet.{/if}</span>
      <button class="cal-link" onclick={() => openLp(person.counterparty_id)}>Open LP page →</button>
    </div>
  {/each}
{/snippet}

{#if selected}
  {@const past = isPast(selected, now)}
  {@const stage = stageOf(selected)}
  {@const openUrl = safeUrl(selected.open_url)}
  <section class="cal-pane" aria-labelledby="cal-pane-title">
    <header class="cal-dr-h">
      {#if expanded}<button class="cal-back" onclick={() => smallScreen ? closeMeeting() : setExpanded(false)}>← Calendar</button>{/if}
      <div class="cal-dr-top">
        <span class="cal-when">{dayLabel(new Date(selected.start))} · {span(selected)}{past ? ' · past' : ''}</span>
        <span class="cal-dr-acts">
          {#if expanded && openUrl}{@render providerLink(openUrl, `Open in ${providerName(openUrl)}`)}{/if}
          {#if !smallScreen}
            <button class="cal-icon" onclick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Back to the calendar' : 'Open the call page'} title={expanded ? 'Back to the calendar (Esc)' : 'Open the call page'}>
              <svg class="cal-i" viewBox="0 0 24 24" aria-hidden="true"><path d={expanded
                ? 'M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7' : 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7'} /></svg></button>
          {/if}
          <button class="cal-x" bind:this={closeButton} onclick={closeMeeting} aria-label="Close" title="Close">×</button>
        </span>
      </div>
      <h2 id="cal-pane-title">{meetingName(selected)}{#if stage}<span class="status-pill status-pill-grey">{fundStageLabel(stage)}</span>{/if}</h2>
      <p class="cal-dr-sub">{selected.summary}</p>
      <p class="cal-dr-sub">With {selected.investors.map(person => person.display_name).join(', ')}</p>
      <div class="cal-ours">
        <span class="cal-flabel">Ours</span>
        {#each selected.team as email (email)}
          <span class="cal-who">{@render avatar(email, true)}{name(email)}{email === selected.lead ? ' (lead)' : ''}</span>
        {/each}
      </div>
    </header>

    <div class="cal-dr-b">
      {#if expanded}
        <div class="cal-wide">
          <div class="cal-wide-main">{@render callBody(selected, true)}</div>
          <aside class="cal-side" aria-label="Evidence and LP record">{@render callSide(selected)}</aside>
        </div>
      {:else}
        {@render callBody(selected, false)}
      {/if}
    </div>

    {#if !expanded}
      <footer class="cal-dr-f">
        {#each selected.investors as person (person.counterparty_id)}
          <button class="cal-link" onclick={() => openLp(person.counterparty_id)}>
            Open {selected.investors.length > 1 ? person.display_name : 'LP page'} →</button>
        {/each}
        {#if openUrl}<span class="cal-dr-ext">{@render providerLink(openUrl, `Open in ${providerName(openUrl)}`)}</span>{/if}
      </footer>
    {/if}
  </section>
{/if}
</div>

<style>
  .cal-page { padding: 4px 0 60px; }
  .cal-bar { display: flex; align-items: center; gap: 10px 20px; flex-wrap: wrap; margin-bottom: 14px; }
  .cal-bar .page-title { margin: 0; line-height: 1; }
  .cal-nav { position: relative; display: flex; align-items: center; gap: 4px; }
  .cal-i { width: 16px; height: 16px; flex: none; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
  .cal-chev { justify-content: center; width: 28px; height: 28px; padding: 0; background: none; border: 0; border-radius: var(--radius-sm); color: var(--text-muted); }
  .cal-chev:hover { background: var(--bg-hover); color: var(--text); }
  .today-mini-btn + .cal-chev { margin-left: 6px; }
  .cal-picker { position: relative; margin-left: 2px; }
  .cal-range { gap: 7px; padding: 4px 8px; background: none; border: 0; border-radius: var(--radius-sm); font-size: 15px; font-weight: 500; color: var(--text); }
  .cal-range .cal-i { color: var(--text-dim); }
  .cal-range:hover, .cal-range[aria-expanded='true'] { background: var(--bg-hover); }
  .cal-range:hover .cal-i, .cal-range[aria-expanded='true'] .cal-i { color: var(--text-muted); }
  .cal-picker-pop { position: absolute; top: calc(100% + 6px); left: 0; z-index: 30; }
  .cal-count { font-size: 12.5px; color: var(--text-dim); white-space: nowrap; }
  .cal-search { position: relative; flex: 0 1 300px; margin-left: auto; }
  .cal-search input { width: 100%; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 8px 12px; font: inherit; font-size: 13px; color: var(--text); }  /* app.css `.page-toolbar-search`, as on Dashboard and Contacts */
  .cal-search input::placeholder { color: var(--text-dim); }
  .cal-search input:focus { outline: none; border-color: var(--border-strong); box-shadow: none; }  /* ambient, like .page-toolbar-search */
  .cal-results { position: absolute; top: calc(100% + 4px); right: 0; z-index: 30; width: min(560px, 92vw); max-height: 60vh; overflow: auto; box-sizing: border-box; padding: 6px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-md); }
  .cal-res-h { margin: 6px 8px 2px; font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: var(--text-dim); }
  .cal-res { display: grid; grid-template-columns: 150px minmax(0, 1fr) auto; gap: 10px; align-items: center; width: 100%; padding: 7px 8px; background: none; border: 0; border-radius: var(--radius-sm); text-align: left; font-size: 12.5px; font-weight: 400; color: var(--text); }
  .cal-res:hover, .cal-res:focus-visible { background: var(--bg-hover); }
  .cal-res-when { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-res-main { min-width: 0; }
  .cal-res-main strong { font-weight: 600; }
  .cal-res-main small { display: block; font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cal-res-lead { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted); white-space: nowrap; }
  .cal-res-note, .cal-res-foot { margin: 6px 8px; font-size: 12px; color: var(--text-dim); }
  .cal-res-foot { margin-top: 4px; padding-top: 6px; border-top: 1px solid var(--border); }
  .cal-seg { display: inline-flex; margin-left: auto; gap: 2px; padding: 2px; border: 1px solid var(--border); border-radius: var(--radius); }
  .cal-seg button { position: relative; gap: 6px; height: 26px; padding: 0 7px; background: none; border: 0; border-radius: var(--radius-sm); font-size: 12.5px; color: var(--text-dim); }
  .cal-seg button:hover { background: none; color: var(--text-muted); }
  .cal-seg button.on { padding: 0 10px 0 8px; background: var(--bg-hover); color: var(--text); }
  .cal-seg button:not(.on):is(:hover, :focus-visible)::after { content: attr(data-tip); position: absolute; top: calc(100% + 6px); left: 50%; z-index: 30; transform: translateX(-50%); padding: 3px 8px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-sm); box-shadow: var(--shadow-md); font-size: 11.5px; font-weight: 400; color: var(--text); white-space: nowrap; pointer-events: none; }

  .cal-filters { display: flex; align-items: center; gap: 8px 16px; flex-wrap: wrap; margin-bottom: 12px; }
  .cal-people { display: flex; align-items: center; gap: 6px 14px; flex-wrap: wrap; }
  .cal-legend { display: flex; align-items: center; flex-wrap: wrap; font-size: 12px; color: var(--text-dim); }
  .cal-flabel { font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: var(--text-dim); margin-right: 4px; }
  .cal-unread { font-size: 12px; color: var(--text-dim); margin: 0 0 12px; }
  .cal-demo { font-size: 12px; color: var(--text-muted); border: 1px dashed var(--border-strong); border-radius: var(--radius); padding: 7px 11px; margin: 0 0 12px; }

  .cal-av { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; font-size: 8.5px; font-weight: 700; color: #141019; flex: none; }
  .cal-av.sm { width: 15px; height: 15px; font-size: 7.5px; }
  .cal-spark { color: var(--accent-ink); }
  .cal-link { font: inherit; font-size: 12.5px; font-weight: 600; color: var(--accent-ink); background: none; border: 0; padding: 0; cursor: pointer; white-space: nowrap; }
  .cal-link:hover { text-decoration: underline; text-underline-offset: 3px; }

  .cal-coord { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 10px 14px; margin-bottom: 14px; }
  .cal-coord-h { font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 4px; }
  .cal-coord-row { display: flex; align-items: baseline; gap: 10px; width: 100%; padding: 5px 0; font-size: 13px; font-weight: 400; text-align: left; color: var(--text-muted); background: none; border: 0; border-top: 1px solid var(--border); border-radius: 0; }
  .cal-coord-row:first-of-type { border-top: 0; }
  .cal-coord-row:hover { background: none; border-color: var(--border); }
  .cal-coord-row:hover .cal-link { text-decoration: underline; text-underline-offset: 3px; }
  .cal-coord-text { flex: 1; min-width: 0; }
  .cal-coord-more { font-size: 12px; color: var(--text-dim); padding-top: 4px; }
  .cal-word { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: .4px; text-transform: uppercase; font-weight: 700; white-space: nowrap; flex: none; width: 104px; color: var(--orange-ink); }
  .cal-word.red { color: var(--red-ink); }
  .cal-status { color: var(--text-dim); }
  .cal-status.amber { color: var(--orange-ink); }
  .cal-status.red { color: var(--red-ink); }

  .cal-frame { position: relative; }
  .cal-frame[aria-busy='true'] :is(.cal-grid, .cal-month) { opacity: .5; }
  .cal-frame-empty { min-height: 280px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); }
  .cal-loading { position: absolute; top: 88px; left: 50%; z-index: 2; transform: translateX(-50%); display: inline-flex; align-items: center; gap: 10px; padding: 9px 16px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: 999px; box-shadow: var(--shadow-md); font-size: 12.5px; color: var(--text-muted); white-space: nowrap; }
  .cal-scroll { overflow-x: auto; }
  .cal-grid { min-width: 620px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .cal-head, .cal-body { display: grid; grid-template-columns: 56px repeat(var(--days, 5), minmax(0, 1fr)); }
  .cal-head { border-bottom: 1px solid var(--border); }
  .cal-dh { padding: 8px 10px; font-size: 12px; color: var(--text-dim); border-left: 1px solid var(--border); }
  .cal-dh b { display: block; font-size: 17px; font-weight: 600; color: var(--text-muted); line-height: 1.2; }
  .cal-dh.today, .cal-dh.today b { color: var(--text); }
  .cal-dh.picked { color: var(--text); box-shadow: inset 0 -2px 0 var(--text-muted); }  /* the day a jump landed on */
  .cal-dh.past { opacity: .7; }
  .cal-tlabel { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: .4px; text-transform: uppercase; color: var(--text); border: 1px solid var(--border-strong); border-radius: 4px; padding: 0 4px; margin-left: 6px; }
  .cal-hour { height: 56px; font-family: var(--font-mono); font-size: 9.5px; color: var(--text-dim); text-align: right; padding-right: 7px; transform: translateY(-6px); white-space: nowrap; }
  .cal-col { position: relative; border-left: 1px solid var(--border); background: repeating-linear-gradient(to bottom, transparent 0, transparent 55px, var(--border) 55px, var(--border) 56px); }
  .cal-col.past { background-color: color-mix(in srgb, var(--text) 3%, transparent); }
  .cal-ev { position: absolute; box-sizing: border-box; border-radius: var(--radius-sm); background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); padding: 4px 7px; text-align: left; overflow: hidden; display: flex; flex-direction: column; align-items: stretch; gap: 1px; font: inherit; color: inherit; cursor: pointer; transition: border-color .1s; }
  .cal-ev:hover { border-color: var(--border-strong); border-left-color: var(--owner); }
  .cal-ev.mine { background: color-mix(in srgb, var(--text) 7%, var(--bg-surface)); }
  .cal-ev.past { opacity: .62; }
  .cal-ev.sel { outline: 1px solid var(--text-muted); z-index: 2; opacity: 1; }  /* outline above neighbouring lanes */
  .cal-ev-t { font-family: var(--font-mono); font-size: 9.5px; color: var(--text-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-ev-n { padding-right: 34px; font-size: 12px; font-weight: 600; color: var(--text); line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-ev-s { font-size: 10.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-ev.narrow .cal-ev-n { padding-right: 0; }
  .cal-ev-avs { position: absolute; right: 5px; bottom: 4px; display: flex; gap: 2px; }
  .cal-now { position: absolute; left: 0; right: 0; height: 0; border-top: 1.5px solid var(--text); z-index: 3; pointer-events: none; }
  .cal-now::before { content: ""; position: absolute; left: -4px; top: -4.5px; width: 7px; height: 7px; border-radius: 50%; background: var(--text); }
  .cal-now span { position: absolute; left: 4px; top: -15px; font-family: var(--font-mono); font-size: 9.5px; color: var(--text); }
  .cal-empty { font-size: 12.5px; color: var(--text-dim); margin: 12px 2px; }

  .cal-ag-day { margin-bottom: 18px; }
  .cal-ag-dh { display: flex; align-items: baseline; gap: 9px; padding-bottom: 7px; margin-bottom: 6px; border-bottom: 1px solid var(--border); }
  .cal-ag-day.picked .cal-ag-dh { border-bottom: 2px solid var(--text-muted); }
  .cal-month { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .cal-mhead, .cal-mbody { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
  .cal-mhead { border-bottom: 1px solid var(--border); }
  .cal-mhead span { padding: 7px 10px; font-size: 12px; color: var(--text-dim); }
  .cal-mday { display: flex; flex-direction: column; gap: 2px; min-width: 0; min-height: 104px; padding: 4px 4px 6px; border-left: 1px solid var(--border); border-top: 1px solid var(--border); }
  .cal-mday:nth-child(7n + 1) { border-left: 0; }
  .cal-mday:nth-child(-n + 7) { border-top: 0; }
  .cal-mday.past { background-color: color-mix(in srgb, var(--text) 3%, transparent); }  /* same tint as .cal-col.past */
  .cal-mday.out .cal-mnum { color: var(--text-dim); opacity: .6; }
  .cal-mnum { align-self: flex-start; justify-content: center; min-width: 24px; height: 24px; padding: 0 5px; background: none; border: 1px solid transparent; border-radius: 12px; font-size: 12px; font-weight: 600; color: var(--text-muted); }
  .cal-mnum:hover { background: var(--bg-hover); border-color: transparent; }
  .cal-mnum.today { border-color: var(--border-strong); color: var(--text); }
  .cal-mchip { display: block; width: 100%; box-sizing: border-box; padding: 2px 6px; background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); border-radius: var(--radius-sm); text-align: left; font-size: 11.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-mchip:hover { background: var(--bg-hover); border-color: var(--border-strong); border-left-color: var(--owner); }
  .cal-mchip.past { opacity: .62; }
  .cal-mchip-t { font-family: var(--font-mono); font-size: 10px; font-weight: 400; color: var(--text-dim); }
  .cal-mmore { align-self: flex-start; padding: 1px 6px; background: none; border: 0; font-size: 11px; font-weight: 500; color: var(--text-muted); }
  .cal-mmore:hover { background: var(--bg-hover); }
  .cal-ag-dh h3 { font-size: 15px; font-weight: 500; margin: 0; color: var(--text); }
  .cal-ag-row { display: grid; grid-template-columns: 128px minmax(0, 1fr) auto auto; gap: 14px; align-items: center; width: 100%; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); border-radius: var(--radius); padding: 9px 13px; margin-bottom: 6px; text-align: left; font: inherit; font-size: 12px; color: inherit; cursor: pointer; }
  .cal-ag-row:hover { background: var(--bg-hover); }
  .cal-ag-row.past { opacity: .62; }
  .cal-ag-row.sel { position: relative; outline: 1px solid var(--text-muted); z-index: 2; opacity: 1; }  /* same as .cal-ev.sel */
  .cal-ag-t { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-ag-n { min-width: 0; }
  .cal-ag-n strong { font-size: 13.5px; font-weight: 600; color: var(--text); }
  .cal-ag-n small { display: block; font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cal-ag-avs { display: flex; gap: 3px; }
  .cal-ag-none { padding: 6px 2px; margin: 0; }
  .status-pill { margin-left: 8px; vertical-align: 2px; }

  /* The calendar and the call pane sit side by side; the call page hides the calendar instead. */
  .cal-shell { display: flex; align-items: flex-start; gap: 18px; }
  .cal-page { flex: 1; min-width: 0; }
  .cal-shell.expanded .cal-page { display: none; }
  .cal-shell.docked .cal-grid { min-width: 480px; }  /* five days in the room left beside the pane */
  /* Sticky inside .main, the scroller: 16px below its top, ending above .main's 28px bottom pad. */
  .cal-pane { flex: 0 0 clamp(380px, 30vw, 440px); position: sticky; top: 16px; height: calc(100dvh - 52px); min-width: 0; display: flex; flex-direction: column; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .cal-shell.expanded .cal-pane { flex: 1; position: static; height: auto; background: none; border: 0; border-radius: 0; overflow: visible; }
  .cal-shell.expanded :is(.cal-dr-h, .cal-dr-b) { width: 100%; max-width: 1080px; margin: 0 auto; padding-left: 0; padding-right: 0; box-sizing: border-box; }
  .cal-dr-h { padding: 16px 18px 12px; border-bottom: 1px solid var(--border); }
  .cal-dr-top { display: flex; align-items: center; gap: 8px; }
  .cal-dr-acts { margin-left: auto; display: flex; align-items: center; gap: 8px; }
  .cal-when { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-icon { justify-content: center; width: 28px; height: 28px; padding: 0; background: none; border: 0; border-radius: var(--radius-sm); color: var(--text-dim); }
  .cal-icon:hover { background: var(--bg-hover); color: var(--text); }
  .cal-x { background: none; border: 0; color: var(--text-dim); font-size: 18px; line-height: 1; padding: 2px 4px; cursor: pointer; }
  .cal-x:hover { color: var(--text); }
  .cal-back { padding: 2px 0; margin-bottom: 12px; background: none; border: 0; font-size: 12.5px; font-weight: 500; color: var(--text-muted); }
  .cal-back:hover { background: none; color: var(--text); }
  .cal-pane h2 { font-size: 18px; font-weight: 650; margin: 6px 0 2px; color: var(--text); overflow-wrap: anywhere; }
  .cal-shell.expanded .cal-pane h2 { font-size: 24px; }
  .cal-dr-sub { font-size: 12.5px; color: var(--text-muted); margin: 2px 0 0; overflow-wrap: anywhere; }
  .cal-ours { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 8px; font-size: 12px; color: var(--text-muted); }
  .cal-who { display: inline-flex; align-items: center; gap: 5px; margin-right: 6px; }
  .cal-dr-b { padding: 14px 18px; overflow-y: auto; flex: 1; }
  .cal-shell.expanded .cal-dr-b { padding-top: 22px; overflow: visible; }
  /* The one thing this call needs from you, above everything else (amber: needs you; red: declined). */
  .cal-job { border: 1px solid var(--border); border-left: 3px solid var(--orange); border-radius: var(--radius); background: var(--bg-hover); padding: 11px 13px; margin-bottom: 16px; font-size: 12.5px; color: var(--text-muted); }
  .cal-job.red { border-left-color: var(--red); }
  .cal-job-h { display: flex; align-items: baseline; gap: 9px; margin-bottom: 4px; }
  .cal-job-h strong { font-size: 14px; font-weight: 600; color: var(--text); }
  .cal-job .cal-word { width: auto; }
  .cal-job p { margin: 0 0 8px; }
  .cal-lbl { display: block; font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: var(--text-dim); margin: 0 0 8px; }
  .cal-gap { margin-top: 16px; }
  .cal-brief :global(.md-content h2) { font-size: 14px; margin: 14px 0 6px; }
  .cal-brief + .cal-brief { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }
  .cal-brief :global(.cal-ref) { margin-left: 2px; font-family: var(--font-mono); font-size: 9px; color: var(--text-dim); cursor: help; }
  /* Docked: one short label per section of the brief. */
  .cal-bgrid { display: grid; grid-template-columns: 96px minmax(0, 1fr); gap: 12px 14px; margin: 0; font-size: 13px; }
  .cal-bgrid dt { font-size: 12px; color: var(--text-dim); padding-top: 1px; }
  .cal-bgrid dd { margin: 0; min-width: 0; }
  .cal-bgrid :global(:is(p, ul, ol)) { margin: 0 0 4px; }
  .cal-bgrid :global(:is(ul, ol)) { padding-left: 16px; }
  .cal-brief-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  /* Call page: the five numbered questions, as in the call brief PDF, evidence beside them. */
  .cal-wide { display: grid; grid-template-columns: minmax(0, 1fr) clamp(220px, 26%, 280px); gap: clamp(24px, 3vw, 40px); }
  .cal-side { position: sticky; top: 16px; align-self: start; display: flex; flex-direction: column; gap: 20px; }
  .cal-bsec { padding: 14px 0 4px; border-top: 1px solid var(--border); }
  .cal-bsec:first-of-type { border-top: 0; padding-top: 2px; }
  .cal-bsec h3 { display: flex; align-items: baseline; gap: 12px; margin: 0 0 8px; font-size: 15.5px; font-weight: 600; color: var(--text); }
  .cal-bnum { width: 20px; flex: none; font-family: var(--font-mono); font-size: 11px; font-weight: 400; color: var(--text-dim); }
  .cal-bbody { padding-left: 32px; font-size: 14px; line-height: 1.6; }
  .cal-srcs { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-muted); }
  .cal-srcs a { color: inherit; }
  .cal-side-card { display: flex; flex-direction: column; gap: 7px; padding: 12px 13px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius); font-size: 12.5px; color: var(--text-muted); }
  .cal-side-card .cal-lbl { margin: 0; }
  .cal-side-card b { font-weight: 600; color: var(--text); }
  .cal-side-card .cal-link { align-self: flex-start; }
  .cal-muted { font-size: 12.5px; color: var(--text-dim); font-style: italic; margin: 0 0 12px; }
  .cal-meta { font-size: 11.5px; color: var(--text-dim); margin: 10px 0 0; }
  .cal-sources { font-size: 12px; color: var(--text-dim); margin-top: 8px; }
  .cal-sources summary { cursor: pointer; margin-bottom: 6px; }
  .cal-note { background: var(--bg-hover); border-radius: var(--radius); padding: 9px 11px; margin-bottom: 8px; font-size: 12.5px; }
  .cal-note-h { display: flex; justify-content: space-between; gap: 10px; color: var(--text); }
  .cal-note-h span { color: var(--text-dim); font-size: 11.5px; white-space: nowrap; }
  .cal-note p { margin: 4px 0 0; color: var(--text-muted); white-space: pre-wrap; overflow-wrap: anywhere; }
  .cal-select, .cal-textarea, .cal-input { width: 100%; box-sizing: border-box; font: inherit; font-size: 13px; color: var(--text); background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: 8px 10px; margin-bottom: 8px; }
  .cal-textarea { resize: vertical; min-height: 96px; }
  .cal-step-l { display: block; margin: 4px 0 6px; font-size: 12px; font-weight: 600; color: var(--text); }
  .cal-step-l span { font-weight: 400; color: var(--text-dim); }
  .cal-step { display: flex; gap: 6px; }
  .cal-step .cal-input { margin-bottom: 0; min-width: 0; }
  .cal-input.cal-date { flex: none; width: 150px; }
  .cal-input:disabled { opacity: .5; }
  .cal-step + .cal-meta { margin: 6px 0 10px; }
  .cal-next { display: flex; align-items: baseline; gap: 10px; padding: 9px 11px; background: var(--bg-hover); border-radius: var(--radius); font-size: 12.5px; color: var(--text); }
  .cal-due { margin-left: auto; font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); white-space: nowrap; }
  .cal-next + .cal-meta { margin-top: 6px; }
  .cal-error { font-size: 12px; color: var(--red-ink); margin: 0 0 8px; }
  .cal-actions { display: flex; gap: 8px; }
  .cal-dr-f { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 12px 18px; border-top: 1px solid var(--border); }
  .cal-dr-ext { margin-left: auto; }

  @media (max-width: 900px) {
    .cal-wide { grid-template-columns: minmax(0, 1fr); }  /* evidence under the brief */
    .cal-side { position: static; }
    .cal-bbody { padding-left: 0; }
  }

  @media (max-width: 640px) {
    .cal-ag-row { grid-template-columns: minmax(0, 1fr) auto; }
    .cal-ag-t { grid-column: 1 / -1; }
    .cal-word { width: auto; }
    .cal-coord-row { flex-wrap: wrap; }
    .cal-search { flex-basis: 100%; margin-left: 0; }
    .cal-results { left: 0; right: auto; width: 100%; }
    .cal-res { grid-template-columns: minmax(0, 1fr) auto; }
    .cal-res-when { grid-column: 1 / -1; }
    .cal-mday { min-height: 64px; padding: 3px 2px; }
    .cal-mhead span { padding: 6px 4px; font-size: 11px; }
    .cal-mchip { height: 6px; padding: 0; border: 0; border-radius: 3px; background: var(--owner); font-size: 0; }  /* a bar per meeting; the name stays as its label */
    .cal-mchip-t { font-size: 0; }
    .cal-picker { position: static; }  /* anchor to .cal-nav so the picker opens from the left edge */
    .cal-mmore { font-size: 10px; padding: 0 2px; }
  }
</style>
